/**
 * Формирование PDF на клиенте без внешних библиотек.
 *
 * Зачем: на сервере нет ни weasyprint/wkhtmltopdf, ни reportlab, ни шрифтов
 * (проверено), а ставить их нельзя — PyPI недоступен. Поэтому документ
 * рисуется в браузере на <canvas> (там системные шрифты с кириллицей),
 * страницы кодируются в JPEG и упаковываются в валидный PDF (/DCTDecode).
 *
 * Это даёт нормальный PDF с русским текстом: его можно приложить к письму
 * и открыть в любой программе. Минус — текст растровый (не выделяется
 * мышью), для печатных форм отправки по почте это приемлемо.
 */

const PX = 2; // пикселей на пункт PDF (≈144 dpi)
const PAGE_W = 595.28; // A4, pt
const PAGE_H = 841.89;
const MARGIN_X = 46;
const MARGIN_TOP = 46;
const MARGIN_BOTTOM = 52;

const LINE_STYLES = {
  title: { size: 16, bold: true, color: "#05227a", spaceBefore: 0, spaceAfter: 10 },
  h2: { size: 12, bold: true, color: "#05227a", spaceBefore: 12, spaceAfter: 6 },
  h3: { size: 11, bold: true, color: "#22304a", spaceBefore: 10, spaceAfter: 4 },
  text: { size: 10.5, bold: false, color: "#1c2434", spaceBefore: 0, spaceAfter: 0 },
  muted: { size: 9.5, bold: false, color: "#5a6478", spaceBefore: 3, spaceAfter: 0 },
  note: { size: 9, bold: false, color: "#8a93a6", spaceBefore: 0, spaceAfter: 0 },
};

const FONT_FAMILY = '"Manrope","Inter",Arial,Helvetica,sans-serif';

function styleFor(line) {
  const base = LINE_STYLES[line.style] || LINE_STYLES.text;
  return {
    ...base,
    bold: line.bold ?? base.bold,
    size: line.size ?? base.size,
  };
}

function fontOf(style) {
  return `${style.bold ? "700" : "400"} ${style.size * PX}px ${FONT_FAMILY}`;
}

/** Разбивает строку на визуальные строки по ширине. */
function wrapLine(ctx, text, maxWidth) {
  const words = String(text).split(/\s+/).filter(Boolean);
  if (!words.length) return [""];
  const lines = [];
  let current = words[0];
  for (let i = 1; i < words.length; i += 1) {
    const candidate = `${current} ${words[i]}`;
    if (ctx.measureText(candidate).width <= maxWidth) {
      current = candidate;
    } else {
      lines.push(current);
      current = words[i];
    }
  }
  lines.push(current);
  return lines;
}

function paintPage(canvas) {
  canvas.width = Math.round(PAGE_W * PX);
  canvas.height = Math.round(PAGE_H * PX);
  const ctx = canvas.getContext("2d");
  ctx.fillStyle = "#ffffff";
  ctx.fillRect(0, 0, canvas.width, canvas.height);
  ctx.textBaseline = "alphabetic";
  return ctx;
}

function toBase64Binary(dataUrl) {
  const base = String(dataUrl).split(",")[1] || "";
  const binary = atob(base);
  const bytes = new Uint8Array(binary.length);
  for (let i = 0; i < binary.length; i += 1) {
    bytes[i] = binary.charCodeAt(i);
  }
  return bytes;
}

function strBytes(text) {
  const out = new Uint8Array(text.length);
  for (let i = 0; i < text.length; i += 1) {
    out[i] = text.charCodeAt(i) & 0xff;
  }
  return out;
}

function pad10(value) {
  return String(value).padStart(10, "0");
}

/**
 * Собирает PDF из массива JPEG-страниц.
 * @param {Uint8Array[]} images
 * @param {number} width
 * @param {number} height
 * @param {string} title
 */
// Экспортируются для printToPdf.js: там страницы рисуются на canvas,
// а упаковка в PDF та же самая.
export const PDF_PAGE_W = PAGE_W;
export const PDF_PAGE_H = PAGE_H;

function packPdf(images, width, height, title) {
  const chunks = [];
  let length = 0;
  const push = (data) => {
    const bytes = typeof data === "string" ? strBytes(data) : data;
    chunks.push(bytes);
    length += bytes.length;
    return length;
  };

  const pageCount = images.length;
  const firstPageObj = 4; // 1 – каталог, 2 – pages, 3 – info
  const objNumberFor = (index) => firstPageObj + index * 3;
  const contentNumberFor = (index) => firstPageObj + index * 3 + 1;
  const imageNumberFor = (index) => firstPageObj + index * 3 + 2;

  push("%PDF-1.4\n%\xE2\xE3\xCF\xD3\n");

  const offsets = new Array(pageCount * 3 + firstPageObj).fill(0);
  const startObj = (number) => {
    offsets[number] = length;
    push(`${number} 0 obj\n`);
  };
  const endObj = () => push("endobj\n");

  startObj(1);
  push("<< /Type /Catalog /Pages 2 0 R >>\n");
  endObj();

  startObj(2);
  const kids = [];
  for (let i = 0; i < pageCount; i += 1) kids.push(`${objNumberFor(i)} 0 R`);
  push(`<< /Type /Pages /Count ${pageCount} /Kids [${kids.join(" ")}] >>\n`);
  endObj();

  startObj(3);
  const safeTitle = String(title || "Документ").replace(/[()\\]/g, " ");
  push(`<< /Title (${safeTitle}) /Producer (MIS REFRESH) /Creator (MIS REFRESH) >>\n`);
  endObj();

  for (let i = 0; i < pageCount; i += 1) {
    startObj(objNumberFor(i));
    push(
      `<< /Type /Page /Parent 2 0 R /MediaBox [0 0 ${PAGE_W.toFixed(2)} ${PAGE_H.toFixed(2)}] ` +
        `/Resources << /XObject << /Im0 ${imageNumberFor(i)} 0 R >> >> ` +
        `/Contents ${contentNumberFor(i)} 0 R >>\n`
    );
    endObj();

    const content = `q ${PAGE_W.toFixed(2)} 0 0 ${PAGE_H.toFixed(2)} 0 0 cm /Im0 Do Q\n`;
    startObj(contentNumberFor(i));
    push(`<< /Length ${content.length} >>\nstream\n`);
    push(content);
    push("endstream\n");
    endObj();

    const image = images[i];
    startObj(imageNumberFor(i));
    push(
      `<< /Type /XObject /Subtype /Image /Width ${width} /Height ${height} ` +
        `/ColorSpace /DeviceRGB /BitsPerComponent 8 /Filter /DCTDecode ` +
        `/Length ${image.length} >>\nstream\n`
    );
    push(image);
    push("\nendstream\n");
    endObj();
  }

  const xrefOffset = length;
  const total = pageCount * 3 + firstPageObj;
  let xref = `xref\n0 ${total}\n0000000000 65535 f \n`;
  for (let n = 1; n < total; n += 1) {
    xref += `${pad10(offsets[n] || 0)} 00000 n \n`;
  }
  push(xref);
  push(
    `trailer\n<< /Size ${total} /Root 1 0 R /Info 3 0 R >>\nstartxref\n${xrefOffset}\n%%EOF\n`
  );

  const result = new Uint8Array(length);
  let cursor = 0;
  for (const chunk of chunks) {
    result.set(chunk, cursor);
    cursor += chunk.length;
  }
  return new Blob([result], { type: "application/pdf" });
}

/**
 * Рисует документ и возвращает PDF-Blob.
 * @param {Array<{text:string,style?:string,bold?:boolean,size?:number}>} rawLines
 * @param {{title?:string, footer?:string}} options
 */
export async function buildPdfFromLines(rawLines, options = {}) {
  const { title = "Документ", footer = "" } = options;
  const lines = (rawLines || []).filter((line) => line && String(line.text ?? "").length);

  if (typeof document !== "undefined" && document.fonts?.ready) {
    try {
      await document.fonts.ready;
    } catch {
      /* шрифты не критичны — сработает системный фолбэк */
    }
  }

  const canvas = document.createElement("canvas");
  let ctx = paintPage(canvas);
  const maxWidth = (PAGE_W - MARGIN_X * 2) * PX;
  const bottomLimit = (PAGE_H - MARGIN_BOTTOM) * PX;

  let y = MARGIN_TOP * PX;
  const images = [];

  const flush = () => {
    images.push(toBase64Binary(canvas.toDataURL("image/jpeg", 0.92)));
    ctx = paintPage(canvas);
    y = MARGIN_TOP * PX;
  };

  const drawLine = (text, style, indent = 0) => {
    const lineHeight = style.size * 1.6 * PX;
    ctx.font = fontOf(style);
    ctx.fillStyle = style.color;
    const width = maxWidth - indent;
    const parts = wrapLine(ctx, text, width);
    parts.forEach((part, index) => {
      if (y + lineHeight > bottomLimit) {
        // Колонтитул на текущей странице.
        ctx.fillStyle = "#9aa3b5";
        ctx.font = `400 ${8.5 * PX}px ${FONT_FAMILY}`;
        ctx.fillText("МИС РЕФРЕШ", MARGIN_X * PX, (PAGE_H - 24) * PX);
        flush();
      }
      ctx.font = fontOf(style);
      ctx.fillStyle = style.color;
      ctx.fillText(part, MARGIN_X * PX + indent, y + style.size * PX);
      y += lineHeight;
      if (index === parts.length - 1) y += (style.spaceAfter || 0) * PX;
    });
  };

  for (const line of lines) {
    const style = styleFor(line);
    if (style.spaceBefore) {
      y += style.spaceBefore * PX;
      if (y + style.size * 1.6 * PX > bottomLimit) {
        flush();
      }
    }
    drawLine(line.text, style, line.indent ? line.indent * PX : 0);
  }

  if (footer) {
    const style = { ...LINE_STYLES.note };
    y += 8 * PX;
    drawLine(footer, style);
  }

  // Подпись/колонтитул последней страницы.
  ctx.fillStyle = "#9aa3b5";
  ctx.font = `400 ${8.5 * PX}px ${FONT_FAMILY}`;
  ctx.fillText("МИС РЕФРЕШ", MARGIN_X * PX, (PAGE_H - 24) * PX);
  ctx.fillText(
    `Сформирован ${new Date().toLocaleString("ru-RU")}`,
    PAGE_W / 2 * PX,
    (PAGE_H - 24) * PX
  );

  images.push(toBase64Binary(canvas.toDataURL("image/jpeg", 0.92)));

  return packPdf(images, canvas.width, canvas.height, title);
}

/**
 * Извлекает текстовые строки из DOM-узла печатной формы.
 *
 * Не «innerText всего блока» — идём по дереву, чтобы сохранить заголовки
 * и подписи полей отдельными строками, а таблицы склеить через разделитель.
 *
 * @param {Element} root
 * @param {{view?: Window}} options — `view` нужен, когда узел belongs другому
 *   документу (например, печатная форма берётся из iframe): getComputedStyle
 *   тогда берётся у окна этого документа.
 */
export function domToLines(root, options = {}) {
  const lines = [];
  if (!root) return lines;

  const view = options.view || (typeof window !== "undefined" ? window : null);
  const computedStyle = (node) =>
    view && view.getComputedStyle ? view.getComputedStyle(node) : null;

  const push = (text, style) => {
    String(text || "")
      .split(/\r?\n/)
      .map((row) => row.replace(/\s+/g, " ").trim())
      .filter(Boolean)
      .forEach((row, index) => {
        lines.push({
          text: row,
          style: index === 0 ? style : "text",
        });
      });
  };

  const blockChild = (element) => {
    const computed = computedStyle(element);
    const display = computed ? computed.display : "";
    return (
      display === "block" ||
      display === "flex" ||
      display === "grid" ||
      display === "list-item" ||
      display === "table" ||
      display === "table-row-group" ||
      display === "table-header-group" ||
      display === "table-row"
    );
  };

  const walk = (element, style) => {
    for (const child of Array.from(element.children)) {
      if (!child || child.nodeType !== 1) continue;
      // Элементы интерфейса на печатной форме (.no-print — кнопки печати,
      // переключатели языка) в письмо попадать не должны.
      if (child.classList && child.classList.contains("no-print")) continue;
      const computed = computedStyle(child);
      if (computed && (computed.display === "none" || computed.visibility === "hidden")) {
        continue;
      }

      const tag = child.tagName;
      const next = { ...style };

      if (tag === "H1") next.style = "title";
      else if (tag === "H2") next.style = "h2";
      else if (tag === "H3" || tag === "H4") next.style = "h3";
      else if (tag === "SMALL") next.style = "note";
      else if (tag === "STRONG" || tag === "B") next.bold = true;
      else if (
        computed &&
        parseInt(computed.fontWeight, 10) >= 600 &&
        style.style === "text"
      ) {
        next.bold = true;
      }

      if (tag === "TR") {
        const cells = Array.from(child.children)
          .map((cell) => (cell.innerText || "").replace(/\s+/g, " ").trim())
          .filter(Boolean);
        if (cells.length) {
          lines.push({ text: cells.join("   |   "), style: style.bold ? "h3" : "text" });
        }
        continue;
      }

      const hasBlockChildren = Array.from(child.children).some(blockChild);
      const hasText = (child.innerText || "").trim().length > 0;

      if (hasBlockChildren) {
        walk(child, next);
        continue;
      }

      if (hasText) {
        push(child.innerText, next.style || style.style || "text");
        if (next.bold) {
          // жирный акцент внутри строки просто сохраняем текстом
        }
      } else if (tag === "HR") {
        lines.push({ text: "", style: "text" });
      }
    }
  };

  walk(root, { style: "text" });
  // Схлопываем повторяющиеся пустые строки.
  const result = [];
  lines.forEach((line) => {
    if (!line.text && result.length && !result[result.length - 1].text) return;
    result.push(line);
  });
  return result;
}

export { packPdf };
export default buildPdfFromLines;
